import { FC, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library, IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons'; // Import regular icons
import { fab } from '@fortawesome/free-brands-svg-icons'; // Import brand icons
import * as fasIcons from '@fortawesome/free-solid-svg-icons';
import * as farIcons from '@fortawesome/free-regular-svg-icons';
import * as fabIcons from '@fortawesome/free-brands-svg-icons';
import { IconButton, Popover, Box, Typography, Grid, Autocomplete, TextField, makeStyles, Tooltip } from "@material-ui/core";
import ColorPickerField from "./ColorPickerField";
import PencilAlt from "src/icons/PencilAlt";
import { ensureHexColor, transformColorFormat } from "./Helpers";
import Info from "@material-ui/icons/Info";

library.add(fas, far, fab);

type IconProperties = {
  FieldKey: string;
  Icon?: string;
  Label?: string | null;
  Description?: string | null;
  IconName?: string | null;
  IconColor?: string;
  IconType?: string;
  IconSize?: string;
  IsDark?: boolean;
};

type FontProperties = {
  FieldKey: string;
  Label?: string | null;
  Description?: string | null;
  FontFamily?: string;
  FontColor?: string;
  FontWeight?: string;
  FontSize?: string;
  IsDark?: boolean;
};

type CustomDataFieldProps = {
  picker: any;
  data: IconProperties | FontProperties | null;
  fontWeightOptions: any[];
  fontSizeOptions: string[];
  iconSizeOptions: any[];
  handleCustomDataChange: any;
  fontFamilyOptions: any[];
};

const useStyles = makeStyles(() => ({
  colorBox: {
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    marginLeft: '5px',
    marginRight: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    minHeight: '24px'
  },
  customInput: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: '1px solid #ccc',
    borderRadius: '10px',
    padding: '10px',
    minHeight: '56px',
    height: '100%'
  },
  popoverContent: {
    padding: '10px',
    boxShadow: '0px 4px 10px lightgrey',
    width: '340px'
  },
  fixedIcon: {
    width: '22px',
    height: '22px',
    fontSize: '22px'
  },
}));

const CustomDataField: FC<CustomDataFieldProps> = ({ picker, data, fontWeightOptions, iconSizeOptions, fontSizeOptions, handleCustomDataChange, fontFamilyOptions }) => {
  const classes = useStyles();
  const maxLength = 35;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // State for Icon properties
  const [iconColor, setIconColor] = useState(`#${(data as IconProperties)?.IconColor || ''}`);
  const [iconSize, setIconSize] = useState(parseInt((data as IconProperties)?.IconSize || '0'));
  const [iconName, setIconName] = useState((data as IconProperties)?.IconName || '');
  const [iconType, setIconType] = useState((data as IconProperties)?.IconType || '');

  // State for Font properties
  const [fontColor, setFontColor] = useState((data as FontProperties)?.FontColor || '');
  const [fontSize, setFontSize] = useState((data as FontProperties)?.FontSize || '');
  const [fontWeight, setFontWeight] = useState((data as FontProperties)?.FontWeight || '');
  const [fontFamily, setFontFamily] = useState((data as FontProperties)?.FontFamily || '');

  useEffect(() => {
    if (data && 'Icon' in data && data.Icon) {
      const iconKey = Object.keys(fasIcons).find(
        (key) => (fasIcons as any)[key].icon?.[3] === parseInt(data?.Icon || '0', 16).toString(16)
      );
      setIconName(iconKey || '');
      setIconColor(`#${(data as IconProperties)?.IconColor || ''}`);
      setIconSize(parseInt((data as IconProperties)?.IconSize || '0'));
      setIconType((data as IconProperties)?.IconType || '');
    } else if (data && 'FontFamily' in data) {
      setFontColor((data as FontProperties)?.FontColor || '');
      setFontSize((data as FontProperties)?.FontSize || '');
      setFontWeight((data as FontProperties)?.FontWeight || '');
      setFontFamily((data as FontProperties)?.FontFamily || '');
    }
  }, [data]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = async (name: string, newValue: any) => {
    let value = newValue;
    if (name === 'IconSize' || name === 'FontSize') {
      value = newValue;
    } else if (name === 'IconColor' || name === 'FontColor') {
      value = newValue;
    } else if (name === 'FontWeight') {
      value = newValue;
    } else if (name === 'FontFamily') {
      value = newValue.Value;
    } else if (name === 'Icon') {
      const iconData = fasIcons[newValue.key] || farIcons[newValue.key] || fabIcons[newValue.key];
      if (!iconData) {
          return;
      }
      // Extract the Unicode value from the icon data
      const iconUnicode = iconData.icon?.[3]?.toString(16);
      // Save the icon name and Unicode
      await Promise.all([
        handleCustomDataChange( picker.card, picker.category, data.FieldKey, 'IconName',  newValue.key),
        handleCustomDataChange( picker.card, picker.category, data.FieldKey, 'Icon', `0x${iconUnicode}`)
      ]);
      // Update the local state
      setIconName(newValue.key);
      value = iconUnicode;
    } else if (name === 'IconType') {
        value = newValue;
    }

    if (name !== 'Icon') {
      const res = await handleCustomDataChange(picker.card,picker.category,data.FieldKey,name,value);
      // Update local state based on the property updated
      if (res) {
        if (name === 'IconSize') {
          setIconSize(value);
        } else if (name === 'IconColor') {
          setIconColor((prev) => value.startsWith('#') ? value.substring(1) : value);
        } else if (name === 'IconType') {
          setIconType(value);
        } else if (name === 'FontSize') {
          setFontSize(value);
        } else if (name === 'FontColor') {
          setFontColor((prev) => value.startsWith('#') ? value.substring(1) : value);
        } else if (name === 'FontWeight') {
          setFontWeight(value);
        } else if (name === 'FontFamily') {
          setFontFamily(value);
        }
      }
      return res;
    } else {
      return true;
    }
  }; 

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const fasIconOptions = Object.keys(fasIcons).map(key => ({
    key,
    icon: fasIcons[key],
    type: 'solid'
  }));
  
  const farIconOptions = Object.keys(farIcons).map(key => ({
    key,
    icon: farIcons[key],
    type: 'regular'
  }));
  
  const fabIconOptions = Object.keys(fabIcons).map(key => ({
    key,
    icon: fabIcons[key],
    type: 'brand'
  }));
  
  const iconOptions = [...fasIconOptions, ...farIconOptions, ...fabIconOptions];

  // Choose the correct icon set based on iconType
  const getIconDefinition = (iconName: string, iconType: string): IconDefinition | undefined => {
    switch (iconType) {
      case 'solid':
        return fasIcons[iconName];
      case 'regular':
        return farIcons[iconName];
      case 'brand':
        return fabIcons[iconName];
      default:
        return undefined;
    }
  };

  const icon: IconDefinition | undefined = iconName ? getIconDefinition(iconName, iconType) : undefined;

  const isIconField = data && 'Icon' in data;

  return (
    <>
      <div style={{ height: '100%' }}>
        <Box className={classes.customInput}>
          <Box display='flex' flexDirection='column' width="100%">
            {/* Full width label */}
            <Typography variant="subtitle2">
              {data?.Label || 
                (isIconField ? iconName || '' : (data as FontProperties)?.FontFamily || '')}
            </Typography>
              
            {/* The rest of the content */}
            <Box display='flex' flexDirection='row' justifyContent="space-between" alignItems="center">
              <Box display='flex' flexDirection='column'>
                {isIconField ? (
                  <>
                    <Typography variant="caption">
                      <span style={{ fontWeight: 'bold' }}>Color: </span>
                      {transformColorFormat(`#${(data as IconProperties)?.IconColor || ''}`) || 'N/A'}
                    </Typography>
                    <Typography variant="caption">
                      <span style={{ fontWeight: 'bold' }}>Size: </span>
                      {iconSize || 'N/A'}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="caption">
                      <span style={{ fontWeight: 'bold' }}>Color: </span>
                      {transformColorFormat(ensureHexColor(fontColor)) || 'N/A'}
                    </Typography>
                    <Typography variant="caption">
                      <span style={{ fontWeight: 'bold' }}>Size: </span>
                      {fontSize || 'N/A'}
                    </Typography>
                    <Typography variant="caption">
                      <span style={{ fontWeight: 'bold' }}>Weight: </span>
                      {fontWeightOptions.find(option => option.Value === fontWeight)?.Label || 'N/A'}
                    </Typography>
                  </>
                )}
              </Box>
              <Box display='flex' flexDirection='row' alignItems='center'>
                {/* Tooltip for description */}
                {((data.Description && data.Description !== "") || data.Label.length > maxLength) && (
                  <Tooltip
                    title={
                      <div>
                        {data.Label.length > maxLength && (
                          <span><b>{data.Label}</b></span>
                        )}
                        {data.Label.length > maxLength && <br />}
                        {data.Description && (
                          <span><i>{data.Description}</i></span>
                        )}
                      </div>
                    }
                  >
                    <IconButton size="small">
                      <Info fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {/* Icon or font color box */}
                {isIconField && icon ? (
                  <Box className={classes.colorBox}>
                    <FontAwesomeIcon
                      icon={icon}
                      style={{ color: transformColorFormat(`#${(data as IconProperties)?.IconColor || ''}`), fontSize: '24px' }}
                      className={classes.fixedIcon}
                    />
                  </Box>
                ) : (
                  fontColor && fontColor !== '' && (
                    <Box
                      className={classes.colorBox}
                      style={{ backgroundColor: fontColor ? transformColorFormat(ensureHexColor(fontColor)) : 'transparent', border: '1px solid black', borderRadius: '4px' }}
                    />
                  )
                )}

                {/* Edit button */}
                <IconButton onClick={handleClick}>
                  <PencilAlt />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorReference="anchorPosition"
          anchorPosition={{ top: window.innerHeight / 2, left: window.innerWidth / 2 }}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          classes={{ paper: classes.popoverContent }}
        >
          {isIconField ? (
            <>
              <Grid item xs={12}>
                <ColorPickerField initialColor={ensureHexColor(iconColor)} handleChange={(type: string, newColor: string) => handleChange('IconColor', newColor)} />
              </Grid>
              <Grid container spacing={0} alignItems="center">
                <Grid item xs={12}  sm={12} md={12} lg={12} xl={12}>
                  <Box width="100%" display="flex" flexDirection="column" padding="10px" paddingBottom={0}>
                    {/* Dropdown for selecting the Icon */}
                    <Autocomplete
                      value={iconOptions.find(option => option.key === iconName) || null}
                      onChange={(event: React.ChangeEvent<{}>, newValue: any) => handleChange('Icon', newValue)}
                      options={iconOptions}
                      getOptionLabel={(option) => option.key}
                      size="small"
                      renderInput={(params) => <TextField {...params} label="Icon" />}
                      fullWidth
                      disableClearable
                      renderOption={(props, option) => (
                        <li {...props} key={`${option.type}-${option.key}`} style={{ display: 'flex', alignItems: 'center' }}>
                          <FontAwesomeIcon icon={option.icon} style={{ marginRight: '8px' }} />
                          {option.key}
                        </li>
                      )}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                  <Box width="100%" display="flex" flexDirection="column" padding="10px" paddingTop={0}>
                    {/* Dropdown for selecting the Icon Size */}
                    <Box marginTop="16px">
                      <Autocomplete
                        options={iconSizeOptions}
                        renderInput={(params) => <TextField {...params} label="Icon Size" variant="outlined" />}
                        onChange={(event, newValue) => handleChange('IconSize', newValue)}
                        getOptionLabel={(option) => option}
                        getOptionSelected={(option, value) => option === value}
                        value={iconSize.toString()}
                        disableClearable
                        size="small"
                      />
                    </Box>

                    {/* Dropdown for selecting the Icon Type */}
                    <Box marginTop="16px">
                      <Autocomplete
                        options={['solid', 'regular', 'light', 'thin', 'brand']}
                        renderInput={(params) => <TextField {...params} label="Icon Type" variant="outlined" />}
                        onChange={(event, newValue) => handleChange('IconType', newValue)}
                        getOptionLabel={(option) => option}
                        getOptionSelected={(option, value) => option === value}
                        getOptionDisabled={(option) => !getIconDefinition(iconName, option)} // Disable options where icon is not available
                        value={iconType}
                        disableClearable
                        size="small"
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* Preview Section */}
                <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
                    <Typography align="center">Preview:</Typography>
                    <Box display="flex" alignItems="center" justifyContent="center" flexGrow={1}>
                      <FontAwesomeIcon
                        icon={icon}
                        style={{ color: transformColorFormat(`#${data.IconColor}`), fontSize: `${iconSize}px` }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <ColorPickerField initialColor={ensureHexColor(fontColor)} handleChange={(type: string, newColor: string) => handleChange('FontColor', newColor)} />
              </Grid>
              {(fontFamily || fontWeight || fontSize) && <Grid container spacing={1}>
                {fontFamily && (<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Autocomplete
                    options={fontFamilyOptions}
                    renderInput={(params) => <TextField {...params} label="Font Family" variant="outlined" />}
                    style={{ marginBottom: '16px' }}
                    onChange={(event, newValue) => handleChange('FontFamily', newValue)}
                    value={fontFamilyOptions.find((option: any) => option.Value === fontFamily) || null}
                    getOptionLabel={(option) => option.Label}
                    getOptionSelected={(option, value) => option.Value === fontFamily}
                    disableClearable
                    size="small"
                  />
                </Grid>)}
                {fontWeight && (<Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                  <Autocomplete
                    options={fontWeightOptions}
                    getOptionLabel={(option) => option.Label}
                    value={fontWeightOptions.find(option => option.Value === fontWeight) || null}
                    onChange={(_, newValue) => handleChange('FontWeight', newValue ? newValue.Value : '')}
                    getOptionSelected={(option, value) => option.Value === value.Value}
                    renderInput={(params) => <TextField {...params} label="Font Weight" variant="outlined" />}
                    fullWidth
                    disableClearable
                    size="small"
                  />
                </Grid>)}
                {fontSize && (<Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                  <Autocomplete
                    options={fontSizeOptions}
                    getOptionLabel={(option) => option}
                    value={fontSizeOptions.find(x => x === fontSize.toString())}
                    onChange={(_, newValue) => handleChange('FontSize', newValue)}
                    renderInput={(params) => <TextField {...params} label="Font Size" variant="outlined" />}
                    fullWidth
                    disableClearable
                    size="small"
                  />
                </Grid>)}
              </Grid>}
            </>
          )}
        </Popover>
      </div>
    </>
  );
};

export default CustomDataField;
